import { z } from 'zod';

import { DRIVE_TYPES, FILTERABLE_DAMAGES, FUEL_TYPES, RUN_CONDITIONS, VEHICLE_COLORS } from '@/api';

/**
 * Стан каталогу живе в URL (§11 гайду): посилання з фільтрами можна переслати
 * колезі, і воно відкриється в тому самому вигляді. Схема на межі маршруту —
 * єдине джерело правди і для типів, і для валідації.
 *
 * Масиви TanStack Router серіалізує в URL як JSON (?fuel=["Diesel"]).
 * Виглядає не найкрасивіше, зате надійно проходить туди й назад разом із
 * типами — власний серіалізатор тут дав би більше ризику, ніж користі.
 *
 * Старі посилання мусять відкриватись: значення, якого вже немає в переліку
 * (`fuel=["gasoline"]` з часів, коли фільтри рахувались на клієнті), тихо
 * відкидається, а не валить сторінку помилкою валідації.
 */

/** Один рядок або масив → масив. */
const toList = (value: unknown): unknown => (value === undefined || Array.isArray(value) ? value : [value]);

/** Масив або рядок → перший рядок: марка й модель тепер по одній, а старі посилання несуть масив. */
const toFirst = (value: unknown): unknown => (Array.isArray(value) ? (value as unknown[])[0] : value);

/** Лише значення з переліку; чуже зі старого посилання відкидається, а не ламає маршрут. */
const knownOnly =
	(allowed: readonly string[]): ((value: unknown) => unknown) =>
	(value: unknown): unknown => {
		const list = toList(value);
		if (!Array.isArray(list)) return list;
		const kept = list.filter((item): item is string => typeof item === 'string' && allowed.includes(item));
		return kept.length > 0 ? kept : undefined;
	};

const csv = z.preprocess(toList, z.array(z.string().min(1)).optional().catch(undefined));

/**
 * Марка й модель — ключі каталогу капсом (TOYOTA, CAMRY), як їх віддає
 * провайдер. Посилання з дашборда й головної несуть те, що набрав дилер
 * («Toyota»), тож регістр вирівнюється тут, на межі.
 */
const upperOne = z.preprocess(
	(value) => {
		const first = toFirst(value);
		return typeof first === 'string' ? first.trim().toUpperCase() || undefined : first;
	},
	z.string().min(1).optional().catch(undefined),
);

export const catalogueSearchSchema = z.object({
	// текстові
	q: z.string().min(1).optional().catch(undefined),

	// те, що фільтрує провайдер
	platform: z.preprocess(toFirst, z.enum(['copart', 'iaai']).optional().catch(undefined)),
	make: upperOne,
	model: upperOne,
	/** Сімейство моделей (SERIES5, X5) — для марок із довідником сімейств замість `model`. */
	series: upperOne,
	damage: z.preprocess(knownOnly(FILTERABLE_DAMAGES), z.array(z.enum(FILTERABLE_DAMAGES)).optional()),
	fuel: z.preprocess(knownOnly(FUEL_TYPES), z.array(z.enum(FUEL_TYPES)).optional()),
	drive: z.preprocess(knownOnly(DRIVE_TYPES), z.array(z.enum(DRIVE_TYPES)).optional()),
	color: z.preprocess(knownOnly(VEHICLE_COLORS), z.array(z.enum(VEHICLE_COLORS)).optional()),
	runCondition: z.preprocess(toFirst, z.enum(RUN_CONDITIONS).optional().catch(undefined)),
	yearFrom: z.coerce.number().int().min(1900).max(2100).optional().catch(undefined),
	yearTo: z.coerce.number().int().min(1900).max(2100).optional().catch(undefined),
	odometerTo: z.coerce.number().int().min(0).optional().catch(undefined),
	priceFrom: z.coerce.number().int().min(0).optional().catch(undefined),
	priceTo: z.coerce.number().int().min(0).optional().catch(undefined),
	buyNow: z.coerce.boolean().optional().catch(undefined),
	hasKeys: z.coerce.boolean().optional().catch(undefined),

	// те, що звужує вже завантажені лоти: провайдер так фільтрувати не вміє
	/** Рівень титулу рахує наш класифікатор з назви документа — у провайдера такого фільтра немає. */
	titleTier: csv,
	/** Фільтр штату в провайдера зламаний (apibara-findings.md, п.10) — звужуємо самі. */
	state: csv,
	/**
	 * Бюджет із доставкою до порту призначення (`to`). Зберігається САМЕ те, що ввів дилер, а не
	 * виведена з нього ставка: інакше зміна тарифу мовчки змінила б збережений
	 * пошук. Провайдеру йде як стеля ставки, точно — рахується по кожному лоту.
	 */
	budgetTo: z.coerce.number().int().min(0).optional().catch(undefined),
	/**
	 * Порт призначення для бюджету (UN/LOCODE). Не фільтр, а вподобання: без
	 * нього — порт дилера чи гостя, і «Clear all» його не скидає.
	 */
	to: z.string().regex(/^[A-Z]{2}[A-Z0-9]{3}$/).optional().catch(undefined),

	// подання
	sort: z.enum(['auction', 'bid', 'year', 'odometer']).catch('auction').default('auction'),
	dir: z.enum(['asc', 'desc']).catch('asc').default('asc'),
	view: z.enum(['grid', 'table']).catch('grid').default('grid'),
});

export type CatalogueSearch = z.infer<typeof catalogueSearchSchema>;

/** Поля, що звужують вибірку. Подання сюди не входить. */
export const FILTER_KEYS = [
	'q', 'platform', 'make', 'model', 'series', 'damage', 'fuel', 'drive', 'color', 'runCondition', 'yearFrom', 'yearTo',
	'odometerTo', 'priceFrom', 'priceTo', 'buyNow', 'hasKeys', 'titleTier', 'state', 'budgetTo',
] as const satisfies readonly (keyof CatalogueSearch)[];

export function countActiveFilters(search: CatalogueSearch): number {
	return FILTER_KEYS.reduce((n, key) => {
		const value = search[key];
		if (value === undefined || value === false) return n;
		return n + (Array.isArray(value) ? value.length : 1);
	}, 0);
}

/** Скидання фільтрів зберігає подання: спосіб перегляду — не фільтр. */
export function clearedSearch(search: CatalogueSearch): CatalogueSearch {
	return { sort: search.sort, dir: search.dir, view: search.view, to: search.to };
}

/*
  Сортування — одним списком людських назв, а не парою «поле + стрілка
  напрямку». Назви — клієнта (Notion «Каталог аукціонів», 2026-09-28): за
  датою торгів «Earliest first» / «Latest first», за роком — «Year».

  Провайдер сортування не налаштовує: він віддає лоти за датою торгів, а
  решта порядків переставляє лише ВЖЕ завантажене (CLAUDE.md, «Джерело
  аукціонних даних»).
*/
export const SORTS = [
	{ value: 'auction:asc', label: 'Earliest first' },
	{ value: 'auction:desc', label: 'Latest first' },
	{ value: 'bid:asc', label: 'Lowest bid' },
	{ value: 'bid:desc', label: 'Highest bid' },
	{ value: 'year:desc', label: 'Year' },
	{ value: 'odometer:asc', label: 'Lowest mileage' },
] as const;

export function sortLabel(search: CatalogueSearch): string {
	return SORTS.find((s) => s.value === `${search.sort}:${search.dir}`)?.label ?? 'Custom order';
}
